import { parseDate } from '@travelata-libs/utils';
import type { LowPriceDay } from '@/shared/api/searchLatest';
import type { DateRange } from '@/shared/model';
import { LOW_PRICE_CALENDAR_MONTHS, LOW_PRICE_CALENDAR_TEXTS, LOW_PRICE_CALENDAR_WEEKDAYS } from '../config/texts';
import type { LowPriceCalendarDay } from '../model/types';

const NBSP = ' ';
const WHITESPACE_RE = /\s/g;
const RU_NUMBER_FORMAT = new Intl.NumberFormat('ru-RU');
const DAY_NUMBER_DIGITS = 2;
const SUNDAY_INDEX_IN_MONDAY_FIRST_WEEK = 6;

function hasPrice(price: number | null): price is number {
  return typeof price === 'number' && Number.isFinite(price) && price > 0;
}

function toMondayFirstWeekdayIndex(sundayFirstWeekday: number): number {
  return sundayFirstWeekday === 0 ? SUNDAY_INDEX_IN_MONDAY_FIRST_WEEK : sundayFirstWeekday - 1;
}

function formatDayLabel(date: string): string {
  const parsedDate = parseDate(date);

  if (!parsedDate) return date;

  const dayNumber = String(parsedDate.getDate()).padStart(DAY_NUMBER_DIGITS, '0');
  const month = LOW_PRICE_CALENDAR_MONTHS[parsedDate.getMonth()];
  const weekday = LOW_PRICE_CALENDAR_WEEKDAYS[toMondayFirstWeekdayIndex(parsedDate.getDay())];

  return `${dayNumber} ${month}, ${weekday}`;
}

function formatPriceLabel(price: number | null): string {
  if (!hasPrice(price)) return LOW_PRICE_CALENDAR_TEXTS.noPrice;

  const amount = RU_NUMBER_FORMAT.format(price).replaceAll(WHITESPACE_RE, NBSP);

  return `${LOW_PRICE_CALENDAR_TEXTS.pricePrefix}${NBSP}${amount}${NBSP}${LOW_PRICE_CALENDAR_TEXTS.currency}`;
}

export function toLowPriceCalendarDays(
  days: readonly LowPriceDay[],
  selectedDate: string | null,
): LowPriceCalendarDay[] {
  const prices = days.map((day) => day.price).filter((price): price is number => hasPrice(price));
  const minPrice = prices.length ? Math.min(...prices) : null;
  const maxPrice = prices.length ? Math.max(...prices) : null;

  return days.map((day) => {
    const price = hasPrice(day.price) ? day.price : null;

    return {
      date: day.date,
      label: formatDayLabel(day.date),
      priceLabel: formatPriceLabel(day.price),
      price,
      selected: selectedDate !== null && day.date === selectedDate,
      cheapest: price !== null && price === minPrice,
      priciest: price !== null && price !== minPrice && price === maxPrice,
    };
  });
}

function isInsideDateRange(date: string, dateRange: DateRange): boolean {
  return date >= dateRange.from && date <= dateRange.to;
}

export function getLowPriceMinDate(days: readonly LowPriceDay[], excluded: readonly DateRange[]): string | null {
  const cheapest = days.reduce<{ date: string; price: number } | null>((min, { date, price }) => {
    if (!hasPrice(price) || excluded.some((dateRange) => isInsideDateRange(date, dateRange))) return min;
    if (min && (min.price < price || (min.price === price && min.date <= date))) return min;

    return { date, price };
  }, null);

  return cheapest?.date ?? null;
}
